import { AnimatePresence, motion as m } from 'motion/react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import calendarSvg from '@/assets/icons/calendar.svg'
import settiSvg from '@/assets/icons/setti.svg'
import { useAuthContext } from '@/contexts/auth-context'
import { getAuthenticatedUser } from '@/lib/get-authenticated-user'
import { CacheAdapter } from '@/shared/infra'

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../ui/collapsible'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger as NativeDropdownMenuTrigger,
} from '../ui/dropdown-menu'
import { CollapsibleItem } from './collapsible-item'
import { DropdownMenuTrigger } from './dropdown-menu-trigger'

interface BookingEngineMenuOptionProps {
  opened: boolean
}

export function BookingEngineMenuOption({
  opened,
}: BookingEngineMenuOptionProps) {
  const { t } = useTranslation('sidebar')
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { isBookingIssuer } = useAuthContext()

  const optionsHref = ['/', '/requesties', '/booking/myReservations']
  const shouldBeOpened = optionsHref.includes(pathname)

  const [openMenu, setOpenMenu] = useState(shouldBeOpened)
  const [user, setUser] = useState<Record<string, any> | null>(null)

  useEffect(() => {
    const userData = CacheAdapter.get('userData')

    if (Object.keys(userData).length > 0) {
      setUser(userData)
    } else {
      getAuthenticatedUser()
        .then(user => {
          CacheAdapter.set('userData', user)
          localStorage.removeItem('loginRequired')
          setUser(user)
        })
        .catch(err => {
          console.log(err)
        })
    }
  }, [])

  useEffect(() => {
    if (!optionsHref.includes(pathname)) {
      setOpenMenu(false)
    } else {
      setOpenMenu(true)
    }
  }, [pathname])

  if (!isBookingIssuer) {
    return null
  }

  function validateEmail(email: string | undefined) {
    if (typeof email === 'string' && email) {
      // @kaftee.com
      return email.endsWith('@voetur.com.br')
    }

    return false
  }

  const canShowBatch = validateEmail(user?.attributes.email)

  return (
    <>
      {!opened && (
        <DropdownMenu>
          <DropdownMenuTrigger>
            <img className="size-6" src={calendarSvg} alt="Calendar" />
          </DropdownMenuTrigger>

          <DropdownMenuContent
            side="right"
            sideOffset={24}
            align="start"
            className="bg-primaryDark border-none p-2 rounded-[5px]"
          >
            <DropdownMenuItem
              onClick={() => navigate('/')}
              className="px-4 text-white hover:bg-white/10 rounded-md"
            >
              {t('--menu-options-search')}
            </DropdownMenuItem>

            <DropdownMenuItem
              onClick={() => navigate('/booking/myReservations')}
              className="px-4 text-white hover:bg-white/10 rounded-md"
            >
              {t('--menu-options-booking')}
            </DropdownMenuItem>

            {canShowBatch && (
              <div className="pl-4 gap-4 pr-2 h-10 flex justify-between rounded-md items-center text-white hover:bg-white/10 has-[button[data-state=open]]:bg-white/10">
                <span>{t('--menu-sections-batch-bookings')}</span>

                <DropdownMenu>
                  <NativeDropdownMenuTrigger className="size-6 flex items-center justify-center rounded-[4px] hover:bg-white/25 data-[state=open]:bg-white/25">
                    <img src={settiSvg} alt="Setti" />
                  </NativeDropdownMenuTrigger>

                  <DropdownMenuContent
                    side="right"
                    sideOffset={24}
                    align="start"
                    className="bg-primaryDark w-[200px] border-none p-2 rounded-[5px]"
                  >
                    <DropdownMenuItem
                      onClick={() =>
                        navigate('/batch-reservations/uploaded-files')
                      }
                      className="px-4 text-white hover:bg-white/10 rounded-md"
                    >
                      {t('--menu-options-uploaded-files')}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate('/batch-reservations/requests')}
                      className="px-4 text-white hover:bg-white/10 rounded-md"
                    >
                      {t('--menu-options-requests')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}

            {/* /batch-reservations/uploaded-files */}
            <DropdownMenuItem
              onClick={() => navigate('/requesties')}
              className="px-4 text-white hover:bg-white/10 rounded-md"
            >
              {t('--menu-options-solicitations')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {opened && (
        <Collapsible
          open={openMenu}
          onOpenChange={setOpenMenu}
          defaultOpen={shouldBeOpened}
          className="group"
        >
          <CollapsibleTrigger className="group bg-[#3E54C0] enabled:hover:brightness-110 h-10 rounded-md duration-200 w-full px-2 flex items-center gap-2 mb-0 data-[state=open]:brightness-110 ">
            <img className="size-6" src={calendarSvg} alt="Calenadar" />

            <AnimatePresence>
              {opened && (
                <m.span
                  initial={{ y: 20, opacity: 0 }}
                  exit={{
                    y: 20,
                    opacity: 0,
                    transition: { duration: 0.2 },
                  }}
                  animate={{
                    y: 0,
                    opacity: 1,
                    transition: { duration: 0.2, delay: 0.2 },
                  }}
                  className="flex-1 text-left line-clamp-1"
                >
                  Booking Engine
                </m.span>
              )}
            </AnimatePresence>

            <img
              className="size-2 rotate-90 group-data-[state=open]:rotate-[270deg] duration-300"
              src={settiSvg}
              alt="Setti"
            />
          </CollapsibleTrigger>

          <CollapsibleContent className="text-base mt-2">
            <CollapsibleItem to="/">
              {t('--menu-options-search')}
            </CollapsibleItem>

            <CollapsibleItem to="/booking/myReservations">
              {t('--menu-options-booking')}
            </CollapsibleItem>

            {canShowBatch && (
              <div className="pl-4 gap-4 pr-2 h-10 flex justify-between rounded-md items-center text-white hover:bg-white/10 has-[button[data-state=open]]:bg-white/10">
                <span>{t('--menu-sections-batch-bookings')}</span>

                <DropdownMenu>
                  <NativeDropdownMenuTrigger className="size-6 flex items-center justify-center rounded-[4px] hover:bg-white/25 data-[state=open]:bg-white/25">
                    <img src={settiSvg} alt="Setti" />
                  </NativeDropdownMenuTrigger>

                  <DropdownMenuContent
                    side="right"
                    sideOffset={32}
                    alignOffset={-8}
                    align="start"
                    className="bg-primaryDark w-[200px] border-none p-2 rounded-[5px]"
                  >
                    <DropdownMenuItem
                      onClick={() =>
                        navigate('/batch-reservations/uploaded-files')
                      }
                      className="px-4 text-white hover:bg-white/10 rounded-md"
                    >
                      {t('--menu-options-uploaded-files')}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate('/batch-reservations/requests')}
                      className="px-4 text-white hover:bg-white/10 rounded-md"
                    >
                      {t('--menu-options-requests')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}

            <CollapsibleItem to="/requesties">
              {t('--menu-options-solicitations')}
            </CollapsibleItem>
          </CollapsibleContent>
        </Collapsible>
      )}
    </>
  )
}
